/**
 * VÉRAULT Portal — Advisor: Bookings
 *
 * Replatformed (not ported). Editorial bookings ledger with AI-assisted
 * confirmation-email import baked in.  Replaces the legacy bookings page.
 *
 * Operational AI surfaces:
 *  - "Parse a confirmation" — paste email → AI returns booking proposal
 *    advisor reviews + commits (human stays in the loop).
 */
import React from "react";
import { motion } from "framer-motion";
import { ClipboardList, Sparkles, X, ArrowRight } from "lucide-react";
import {
  Surface,
  Heading,
  PageHeader,
  EditorialRule,
  StatusTag,
  Empty,
  Skeleton,
  PortalButton,
  api,
  fmt,
  motion as portalMotion,
} from "../../index";

const { fadeInUp, stagger } = portalMotion;

function statusTone(s) {
  const x = (s || "").toLowerCase();
  if (x === "confirmed" || x === "booked" || x === "completed") return "sage";
  if (x === "cancelled" || x === "overdue") return "terracotta";
  if (x === "pending" || x === "in_progress") return "gold";
  return "neutral";
}

// --- Parse Confirmation modal -------------------------------------
function ParseConfirmation({ open, onClose, onParsed }) {
  const [text, setText] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [proposal, setProposal] = React.useState(null);
  const [trips, setTrips] = React.useState([]);
  const [clients, setClients] = React.useState([]);
  const [tripId, setTripId] = React.useState("");
  const [clientId, setClientId] = React.useState("");

  // Pre-fetch trip + client options the first time the modal opens so the
  // attach step is instant — small payloads, cached by axios.
  React.useEffect(() => {
    if (!open || trips.length) return;
    Promise.all([
      api.get("/travels/trips", { params: { sort_by: "date" } }).then((r) => r.data).catch(() => []),
      api.get("/travels/clients").then((r) => r.data).catch(() => []),
    ]).then(([t, c]) => {
      setTrips(Array.isArray(t) ? t : []);
      setClients(Array.isArray(c) ? c : []);
    });
  }, [open, trips.length]);

  // Auto-populate client_id when a trip is chosen.
  React.useEffect(() => {
    if (!tripId) return;
    const t = trips.find((x) => x.id === tripId);
    if (t?.client_id && !clientId) setClientId(t.client_id);
  }, [tripId, trips, clientId]);

  const run = async () => {
    if (!text.trim()) return;
    setLoading(true);
    setError(null);
    setProposal(null);
    try {
      const { data } = await api.post("/ai/parse-email", { text });
      setProposal(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't read that email.");
    } finally {
      setLoading(false);
    }
  };

  const commit = async () => {
    if (!proposal) return;
    if (!tripId && !clientId) {
      setError("Attach this booking to a trip or client before saving.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const payload = {
        ...proposal,
        trip_id: tripId || proposal.trip_id || null,
        client_id: clientId || proposal.client_id || null,
        auto_spawn_payment: false,
      };
      await api.post("/bookings", payload);
      onParsed && onParsed();
      onClose();
    } catch {
      setError("Couldn't save the booking. Try again.");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (!open) {
      setText("");
      setError(null);
      setProposal(null);
      setTripId("");
      setClientId("");
    }
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[9990] bg-black/30 backdrop-blur-[6px] flex items-start justify-center pt-[10vh] px-4" onClick={onClose} data-testid="parse-modal-backdrop">
      <div
        className="w-full max-w-2xl rounded-2xl bg-[hsl(var(--background))] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.25)] overflow-hidden border border-black/5"
        onClick={(e) => e.stopPropagation()}
        data-testid="parse-modal"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[rgba(0,0,0,0.06)]">
          <div className="flex items-center gap-2">
            <Sparkles className="size-4 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">Concierge import</p>
              <p className="font-serif text-lg">Parse a confirmation</p>
            </div>
          </div>
          <button onClick={onClose} className="size-9 inline-flex items-center justify-center rounded-md text-[hsl(var(--foreground-muted))] hover:bg-[hsl(var(--background-paper))]" aria-label="Close">
            <X className="size-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {!proposal && (
            <>
              <p className="font-accent italic text-[14px] text-[hsl(var(--foreground-secondary))]">
                Paste a hotel, flight, or vendor confirmation email below — I'll lift out the structured pieces for you to review.
              </p>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={10}
                placeholder="Paste the full confirmation email here…"
                data-testid="parse-textarea"
                className="w-full rounded-lg border border-[rgba(0,0,0,0.08)] bg-white/60 px-4 py-3 font-body text-[14px] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent-gold))] resize-none"
              />
              {error && <p className="font-body text-sm text-[hsl(var(--accent-terracotta))]" data-testid="parse-error">{error}</p>}
            </>
          )}

          {proposal && (
            <div className="space-y-5" data-testid="parse-proposal">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                Confidence · {Math.round((proposal.confidence || 0) * 100)}%
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  ["Vendor", proposal.vendor],
                  ["Type", proposal.booking_type],
                  ["Confirmation", proposal.confirmation_number],
                  ["Cost", proposal.quoted_cost ? fmt.money(proposal.quoted_cost, proposal.currency) : null],
                  ["Start", fmt.formatDate(proposal.start_date)],
                  ["End", fmt.formatDate(proposal.end_date)],
                ].map(([k, v]) => (
                  <div key={k}>
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">{k}</p>
                    <p className="mt-1 font-body text-[14px]">{v || <span className="text-[hsl(var(--foreground-muted))]">—</span>}</p>
                  </div>
                ))}
              </div>
              {proposal.description && (
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">Description</p>
                  <p className="mt-1 font-accent italic text-[14px] text-[hsl(var(--foreground-secondary))]">{proposal.description}</p>
                </div>
              )}

              {/* Attach to trip + client — required for commit */}
              <div className="pt-4 border-t border-[rgba(0,0,0,0.06)] space-y-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                  Attach to
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="font-body text-[12px] text-[hsl(var(--foreground-secondary))]">Trip</span>
                    <select
                      value={tripId}
                      onChange={(e) => setTripId(e.target.value)}
                      data-testid="parse-trip-select"
                      className="mt-1 w-full rounded-md border border-[rgba(0,0,0,0.08)] bg-white/70 px-3 py-2 font-body text-[13px] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent-gold))]"
                    >
                      <option value="">— Select a trip —</option>
                      {trips.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.internal_trip_name || t.client_trip_title || t.destinations || t.id}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="font-body text-[12px] text-[hsl(var(--foreground-secondary))]">Client</span>
                    <select
                      value={clientId}
                      onChange={(e) => setClientId(e.target.value)}
                      data-testid="parse-client-select"
                      className="mt-1 w-full rounded-md border border-[rgba(0,0,0,0.08)] bg-white/70 px-3 py-2 font-body text-[13px] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent-gold))]"
                    >
                      <option value="">— Select a client —</option>
                      {clients.map((c) => (
                        <option key={c.id} value={c.id}>{c.full_name || c.email || c.id}</option>
                      ))}
                    </select>
                  </label>
                </div>
                {error && <p className="font-body text-sm text-[hsl(var(--accent-terracotta))]" data-testid="parse-error">{error}</p>}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-[rgba(0,0,0,0.06)] bg-[hsl(var(--background-paper))]/40">
          <PortalButton variant="ghost" size="sm" onClick={onClose} data-testid="parse-cancel">Cancel</PortalButton>
          {!proposal ? (
            <PortalButton variant="primary" size="sm" onClick={run} disabled={!text.trim() || loading} data-testid="parse-submit" icon={<Sparkles className="size-3.5" strokeWidth={1.5} />}>
              {loading ? "Reading…" : "Read the email"}
            </PortalButton>
          ) : (
            <PortalButton variant="gold" size="sm" onClick={commit} disabled={loading || (!tripId && !clientId)} data-testid="parse-commit" iconAfter={<ArrowRight className="size-3.5" strokeWidth={1.5} />}>
              {loading ? "Saving…" : "Save booking"}
            </PortalButton>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Page ---------------------------------------------------------
import { InboxBell, InboxDrawer } from "../../components/Inbox";

export default function AdvisorBookings() {
  const [bookings, setBookings] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [modal, setModal] = React.useState(false);
  const [inboxOpen, setInboxOpen] = React.useState(false);
  const [reloadKey, setReloadKey] = React.useState(0);

  React.useEffect(() => {
    let alive = true;
    api
      .get("/bookings")
      .then(({ data }) => alive && setBookings(Array.isArray(data) ? data : []))
      .catch(() => alive && setError("Couldn't load bookings."));
    return () => {
      alive = false;
    };
  }, [reloadKey]);

  return (
    <>
      <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-10">
        <motion.div variants={fadeInUp}>
          <PageHeader
            eyebrow="Cross-trip · Booking import"
            title="The ledger"
            lede="Hotels, flights, vendors — everything reserved for your clients."
            actions={
              <>
                <InboxBell onClick={() => setInboxOpen(true)} />
                <PortalButton
                  variant="primary"
                  size="md"
                  icon={<Sparkles className="size-3.5" strokeWidth={1.5} />}
                  onClick={() => setModal(true)}
                  data-testid="open-parse-modal"
                >
                  Parse a confirmation
                </PortalButton>
              </>
            }
          />
        </motion.div>

        {/* Wave 36 · PI-9 — bookings now live inside each trip's Travel Operations tab.
            This firm-level page survives as a cross-trip ledger + AI parser intake. */}
        <motion.div variants={fadeInUp}>
          <Surface variant="paper" padded className="!p-4 border-[hsl(var(--accent-gold))]/30" data-testid="bookings-relocation-banner">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
              New home
            </p>
            <p className="font-serif text-[15px] mt-1">
              Bookings now live inside each Experience under <span className="font-mono">Travel Operations</span>.
            </p>
            <p className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))] mt-1">
              This page survives as the cross-trip ledger + the AI confirmation-email parser intake.
              Click any row to open the trip's Travel Operations.
            </p>
          </Surface>
        </motion.div>

        <motion.div variants={fadeInUp}>
          <EditorialRule label={bookings ? `${bookings.length} on the ledger` : "loading"} />
          <div className="mt-5">
            {error ? (
              <Empty title={error} icon={<ClipboardList className="size-6" strokeWidth={1.25} />} />
            ) : !bookings ? (
              <div className="space-y-2">
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-20" />
                ))}
              </div>
            ) : bookings.length === 0 ? (
              <Empty
                title="The ledger is empty"
                hint="Paste your first confirmation and watch it appear."
                icon={<ClipboardList className="size-6" strokeWidth={1.25} />}
                action={
                  <PortalButton size="sm" variant="ghost" onClick={() => setModal(true)} icon={<Sparkles className="size-3.5" strokeWidth={1.5} />}>
                    Parse a confirmation
                  </PortalButton>
                }
              />
            ) : (
              <Surface variant="default" padded={false} className="overflow-hidden">
                <ul className="divide-y divide-[rgba(0,0,0,0.05)]">
                  {bookings.map((b) => (
                    <li key={b.id}>
                      <div className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_200px_140px_100px] gap-3 sm:gap-6 items-center px-5 sm:px-6 py-4" data-testid={`booking-${b.id}`}>
                        <div className="min-w-0">
                          <p className="font-serif text-lg text-[hsl(var(--foreground))] truncate">
                            {b.vendor || b.description || "Booking"}
                          </p>
                          <p className="mt-0.5 font-accent italic text-[12.5px] text-[hsl(var(--foreground-secondary))] truncate">
                            {[b.booking_type, b.confirmation_number].filter(Boolean).join(" · ")}
                          </p>
                          {b.trip_name && (
                            <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] truncate">
                              {b.trip_name}{b.client_name ? ` · ${b.client_name}` : ""}
                            </p>
                          )}
                        </div>
                        <div className="hidden sm:block font-mono text-[11px] text-[hsl(var(--foreground-muted))]">
                          {fmt.formatDateRange(b.start_date, b.end_date) || "Dates TBD"}
                        </div>
                        <div className="hidden sm:block">
                          <StatusTag tone={statusTone(b.status || b.reservation_status)}>{b.status || b.reservation_status || "draft"}</StatusTag>
                        </div>
                        <span className="text-right font-mono text-[12px] text-[hsl(var(--foreground))]">
                          {b.actual_cost || b.quoted_cost ? fmt.money(b.actual_cost || b.quoted_cost, b.currency) : "—"}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </Surface>
            )}
          </div>
        </motion.div>
      </motion.div>

      <ParseConfirmation
        open={modal}
        onClose={() => setModal(false)}
        onParsed={() => {
          setReloadKey((k) => k + 1);
          window.dispatchEvent(new CustomEvent("verault:inbox-changed"));
        }}
      />
      <InboxDrawer
        open={inboxOpen}
        onClose={() => setInboxOpen(false)}
        onChanged={() => {
          setReloadKey((k) => k + 1);
          window.dispatchEvent(new CustomEvent("verault:inbox-changed"));
        }}
      />
    </>
  );
}
